import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import useUpdatedRef from '../../../utils/react/useUpdatedRef';

import { doesKeyboardOpenOverCanvasViewport } from '../../../utils/keyboard/mobileKeyboardUtils';
import { getOnboardingDisableToolbar } from '../../../workspace/toolbar/selector/toolbarSelector';

import { PlatformDetails } from '../../../../common/platform/platformTypes';
import { RevealKeyboardState } from '../MobileToolbar';

/**
 * Listens to changes in the visual viewport to determine whether keyboard is shown/hidden.
 */
export const useMobileToolbarPositioner = (
    platformDetails: PlatformDetails,
    revealKeyboardState: RevealKeyboardState,
    setRevealKeyboardState: (revealKeyboardState: RevealKeyboardState) => void,
): void => {
    const animationRef = useRef<number | null>(null);
    const onboardingDisableToolbar = useSelector(getOnboardingDisableToolbar);
    const onboardingDisableToolbarRef = useUpdatedRef(onboardingDisableToolbar);

    // A ref to store the current revealKeyboardState, so that we can access it in the event listeners
    const revealKeyboardStateRef = useRef<RevealKeyboardState>(revealKeyboardState);

    useEffect(() => {
        revealKeyboardStateRef.current = revealKeyboardState;
    }, [revealKeyboardState]);

    const setRevealKeyboardStateAndRef = (newRevealKeyboardState: RevealKeyboardState) => {
        // If the toolbar is disabled, don't shift it into view if the keyboard appears
        if (onboardingDisableToolbarRef.current && newRevealKeyboardState !== RevealKeyboardState.NONE) return;

        revealKeyboardStateRef.current = newRevealKeyboardState;
        setRevealKeyboardState(newRevealKeyboardState);
    };

    useEffect(() => {
        const onKeyboardDidShow = () => {
            if (
                revealKeyboardStateRef.current === RevealKeyboardState.PREPARING_REVEAL ||
                revealKeyboardStateRef.current === RevealKeyboardState.REVEAL
            )
                return;

            // Align it to just below the top of the keyboard, so that the animation will be from this point onwards
            if (doesKeyboardOpenOverCanvasViewport(platformDetails)) {
                setRevealKeyboardStateAndRef(RevealKeyboardState.PREPARING_REVEAL);
            }

            animationRef.current = requestAnimationFrame(() => {
                setRevealKeyboardStateAndRef(RevealKeyboardState.REVEAL);
                animationRef.current = null;
            });
        };

        const onKeyboardWillHide = () => {
            if (revealKeyboardStateRef.current === RevealKeyboardState.NONE) return;

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }

            setRevealKeyboardStateAndRef(RevealKeyboardState.NONE);
        };

        window.addEventListener('keyboardDidShow', onKeyboardDidShow);
        window.addEventListener('keyboardWillHide', onKeyboardWillHide);

        return () => {
            window.removeEventListener('keyboardDidShow', onKeyboardDidShow);
            window.removeEventListener('keyboardWillHide', onKeyboardWillHide);
        };
    }, []);
};

export default useMobileToolbarPositioner;
